import { INACTIVE_STATUSES } from '@/lib/projectStatus';
import { isAccessRole, isTeamAccessExpired, namedAsPropertyManager, todayPacific } from '@/lib/projectTeam';
import { daysUntil, tenantDetailsStatus } from '@/lib/tenantLeaseDetails';
import { blankLeaseFields, openTasks } from '@/lib/openItems';

/**
 * My Day: what needs the signed-in person today, across every project, so a
 * Property Manager does not have to open each project to find out.
 *
 * It is the portfolio-wide twin of the Tasks tab's open items (lib/openItems.js)
 * and follows the same rules: the old standard lease task rows are not work,
 * a plan sent back "changes requested" is waiting as much as one sent back
 * "revise and resubmit", and a milestone is work as much as a task is.
 *
 * Everything here is worked out from the lists the Dashboard already loads.
 * The API has already decided which projects the caller may see; this only
 * sorts them. It is not an access check, and the pages it links to still
 * decide what the person may do there.
 *
 * Each item appears once. Anything late goes to Overdue and nowhere else, so
 * the same task or plan set is never counted twice on the page.
 */

/** How far ahead "Coming up" looks, in days after today. */
export const LOOK_AHEAD_DAYS = 7;

export const SCOPE_MINE = 'mine';
export const SCOPE_ALL = 'all';

const ASSIGNMENT_FIELDS = [
  'property_manager',
  'senior_property_manager',
  'apm',
  'construction_manager',
  'project_manager_name',
];

/* Sent back to whoever runs the project, waiting on them to resubmit. */
const PLAN_SENT_BACK = ['revisions_required', 'changes_requested'];

const keyOf = value => String(value ?? '').trim().toLowerCase();

function onTeam(project, email, today) {
  return (Array.isArray(project.team) ? project.team : []).some(m =>
    keyOf(m?.email) === email && isAccessRole(m.role) && !isTeamAccessExpired(m, today));
}

/**
 * Does this project name the person - in one of its five boxes, or through a
 * current Westwood role on its Team tab? Having created it does not count: the
 * Construction Manager creates projects for other people to run, and they are
 * not "mine" for that reason.
 */
export function namesMe(project, user, today = todayPacific()) {
  const email = keyOf(user?.email);
  if (!project || !email) return false;
  return ASSIGNMENT_FIELDS.some(f => keyOf(project[f]) === email) || onTeam(project, email, today);
}

/**
 * Does the person run this project from the Property Manager's side - the one
 * who resubmits a set sent back and fills in the lease details? The Property
 * Manager, Senior Property Manager or APM box, or a current Westwood role on
 * the Team tab. The VP of Property Management runs a project only as its
 * Property Manager, the same rule PlanReview applies.
 */
export function runsProject(project, user, today = todayPacific()) {
  const email = keyOf(user?.email);
  if (!project || !email) return false;
  if (user.role === 'viewer') return false;
  if (user.role === 'vp_property') return namedAsPropertyManager(project, user, today);
  return namedAsPropertyManager(project, user, today)
    || keyOf(project.apm) === email
    || onTeam(project, email, today);
}

/**
 * May the person decide a plan set or approve a milestone on this project?
 * The administrator tiers, or the project's Construction Manager - never the
 * VP of Property Management, whatever box names them. The same test as `isCM`
 * in PlanReview.
 */
export function reviewsProject(project, user) {
  const email = keyOf(user?.email);
  if (!project || !email) return false;
  if (user.role === 'admin' || user.role === 'project_manager') return true;
  if (user.role === 'vp_property' || user.role === 'viewer') return false;
  return keyOf(project.construction_manager) === email;
}

/** Is the task assigned to this person by email or by name? The box is free text. */
function assignedToMe(task, user) {
  const who = keyOf(task.assigned_to);
  if (!who) return false;
  return who === keyOf(user?.email) || (!!keyOf(user?.full_name) && who === keyOf(user.full_name));
}

/** Is the "All projects" switch worth offering? Only to the tiers that see every project. */
export function seesAllProjects(user) {
  return ['admin', 'project_manager', 'vp_property', 'viewer'].includes(user?.role);
}

/**
 * The page's lists:
 *
 *   overdue      tasks, milestones, plan reviews and lease-detail deadlines already past
 *   today        tasks and milestones due today
 *   toReview     plan sets and milestones waiting on a decision, not yet late
 *   sentBack     plan sets and milestones sent back, waiting on the people running the project
 *   leaseBlank   tenant projects whose Lease Details are not finished, not yet late
 *   comingUp     tasks and milestones due in the next LOOK_AHEAD_DAYS days
 *
 * Every item is `{ key, type, project, title, dueDate, days, href }`, where
 * `type` is 'task', 'milestone', 'plan' or 'lease' and `days` is whole days
 * until the due date (negative once late, null for none).
 *
 * `scope` is SCOPE_MINE - the projects that name the person, plus any task
 * assigned to them by name - or SCOPE_ALL, everything the API returned. In
 * SCOPE_MINE a review is listed only for someone who may decide it, and a
 * sent-back set or a blank lease screen only for someone who runs the project.
 *
 * Archived, complete, on hold and canceled projects are left out, as they are
 * from the Dashboard's Active Projects: nobody is expected to be working them.
 */
export function myDay({
  projects = [], tasks = [], milestones = [], plans = [], user, scope = SCOPE_MINE, now = new Date(),
}) {
  const today = todayPacific(now);
  const mine = scope !== SCOPE_ALL;
  const active = projects.filter(p => !p.archived && !INACTIVE_STATUSES.includes(p.status));
  const projectById = new Map(active.map(p => [p.id, p]));
  const mayReview = p => !mine || reviewsProject(p, user);
  const mayRun = p => !mine || runsProject(p, user, today);
  const isMine = p => !mine || namesMe(p, user, today);

  const out = { overdue: [], today: [], toReview: [], sentBack: [], leaseBlank: [], comingUp: [] };

  /* A dated piece of work: late, today, this week, or not yet worth showing. */
  const byDate = item => {
    if (item.days === null) return;
    if (item.days < 0) out.overdue.push(item);
    else if (item.days === 0) out.today.push(item);
    else if (item.days <= LOOK_AHEAD_DAYS) out.comingUp.push(item);
  };

  for (const task of openTasks(tasks)) {
    const project = projectById.get(task.project_id);
    if (!project || !task.due_date) continue;
    if (mine && !namesMe(project, user, today) && !assignedToMe(task, user)) continue;
    byDate({
      key: `task-${task.id}`, type: 'task', project, title: task.name,
      dueDate: task.due_date, days: daysUntil(task.due_date, now),
      href: `/ProjectDetail?id=${project.id}&tab=tasks`,
    });
  }

  for (const m of milestones) {
    const project = projectById.get(m.project_id);
    if (!project) continue;
    const item = {
      key: `milestone-${m.id}`, type: 'milestone', project, title: m.name,
      dueDate: m.planned_finish || null,
      days: m.planned_finish ? daysUntil(m.planned_finish, now) : null,
      href: `/ProjectDetail?id=${project.id}&tab=milestones`,
    };
    if (m.approval_status === 'pending_review') {
      if (mayReview(project)) out.toReview.push({ ...item, detail: 'Milestone waiting for approval' });
    } else if (m.status === 'complete') {
      continue;
    } else if (m.approval_status === 'revisions_required') {
      if (mayRun(project)) out.sentBack.push({ ...item, detail: 'Milestone - revisions required' });
    } else if (isMine(project)) {
      byDate(item);
    }
  }

  for (const plan of plans) {
    const project = projectById.get(plan.project_id);
    if (!project) continue;
    const item = {
      key: `plan-${plan.id}`, type: 'plan', project,
      title: plan.plan_set_name || 'Plan submission',
      kind: plan.discipline === 'signage' ? 'Signs' : 'Plans',
      href: `/PlanReview?id=${plan.id}`,
    };
    if (plan.status === 'in_review') {
      if (!mayReview(project)) continue;
      const days = plan.due_date ? daysUntil(plan.due_date, now) : null;
      const reviewItem = { ...item, dueDate: plan.due_date || null, days, detail: `${item.kind} waiting for review` };
      if (days !== null && days < 0) out.overdue.push(reviewItem);
      else out.toReview.push(reviewItem);
    } else if (PLAN_SENT_BACK.includes(plan.status)) {
      if (!mayRun(project)) continue;
      out.sentBack.push({ ...item, dueDate: null, days: null, detail: `${item.kind} sent back - revise and resubmit` });
    }
  }

  for (const project of active) {
    if (project.type !== 'tenant' || !mayRun(project)) continue;
    const blanks = blankLeaseFields(project);
    if (!blanks.length) continue;
    const status = tenantDetailsStatus(project, now);
    const item = {
      key: `lease-${project.id}`, type: 'lease', project,
      title: `Answer ${blanks.length} lease question${blanks.length === 1 ? '' : 's'}`,
      detail: blanks.join(', '),
      dueDate: status.dueDate, days: status.daysLeft,
      href: `/ProjectDetail?id=${project.id}&tab=lease`,
    };
    if (status.overdue) out.overdue.push(item);
    else out.leaseBlank.push(item);
  }

  /* Soonest (or longest late) first; anything with no date after everything
     with one, then by project name. */
  const order = (a, b) => (a.days ?? Infinity) - (b.days ?? Infinity)
    || String(a.project.name ?? '').localeCompare(String(b.project.name ?? ''));
  for (const list of Object.values(out)) list.sort(order);
  return out;
}

/** How many items need the person now - every list but Coming up. */
export function needsMeCount(day) {
  return day.overdue.length + day.today.length + day.toReview.length
    + day.sentBack.length + day.leaseBlank.length;
}

/** "Due today", "Due tomorrow", "Due in 3 days", "1 day overdue", "4 days overdue". */
export function dueLabel(days) {
  if (days === null || days === undefined) return '';
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days > 1) return `Due in ${days} days`;
  const late = -days;
  return `${late} day${late === 1 ? '' : 's'} overdue`;
}
